import { MonetizationType } from '@/@types/graphql-types';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { WebLocale } from '@/enums/web-locale';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import { useTitleDetailsStore } from './composables/useStores';
import { computed, ComputedRef, onMounted, onServerPrefetch } from 'vue';

type PromotionProviders = 9 | 119 | 337 | 1773 | 1899 | 531 | 350 | 385 | 122;

const promotionProviderIcons: Record<PromotionProviders, string> = {
	9: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	119: `${JW_CONFIG.IMAGESCALER_URL}/icon/52449539/s100/amazonprime.webp`,
	337: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	1773: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	1899: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	531: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	350: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	385: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	122: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
};

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFree = [MonetizationType.Flatrate, MonetizationType.Free];

interface PromoText {
	/** Set `null` to disable. */
	ribbon: string | null;
	/** Set `null` to disable. */
	offerText: string | null;
	/** Set `null` to disable. */
	longText: string | null;
	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;
	/** `YYYY-MM-DD` format. */
	endDate: ISO8601;
	/** The ID of the promoted provider. */
	provider: PromotionProviders;
	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;
	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;
	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

type PromotionCountries = 'ES' | 'GB' | 'AU' | 'ID' | 'TH' | 'MY';

const promotions: Record<PromotionCountries, PromoText[]> = {
	ID: [
		{
			ribbon: 'Langganan Sekarang',
			offerText: 'Promo 75%',
			longText: 'Mulai Rp16ribuan*/bulan',
			startDate: '2025-06-16',
			endDate: '2025-06-26',
			provider: 122,
		},
	],
	TH: [
		{
			ribbon: 'ข้อเสนอ',
			offerText: 'ข้อเสนอจำนวนจำกัดเท่านั้น',
			longText: 'ส่วนลด 50% ต่อเดือน. ข้อกำหนด&เงื่อนไข',
			startDate: '2025-06-16',
			endDate: '2025-06-26',
			provider: 122,
		},
	],
	MY: [
		{
			ribbon: 'Sign Up!',
			offerText: 'Sign Up Now!',
			longText: 'Sign Up to Disney+ Hotstar Today!',
			startDate: '2025-06-16',
			endDate: '2025-06-26',
			provider: 122,
		},
	],
	ES: [
		{
			ribbon: null,
			offerText: null,
			longText: 'Ve a SkyShowtime ahora',
			startDate: '2025-05-09',
			endDate: '2025-06-30',
			provider: 1773,
		},
		{
			ribbon: 'OFERTA',
			offerText: 'OFERTA',
			longText: '1,99€/mes durante 4 meses',
			startDate: '2025-06-11',
			endDate: '2025-06-30',
			provider: 337,
			disclaimerText:
				'Oferta válida hasta el 30/06/25. Solo para nuevos suscriptores y antiguos suscriptores que reúnan los requisitos. Deben ser mayores de 18 años. Después de los 4 meses, tu suscripción se renovará automáticamente al precio mensual del plan Estándar con anuncios vigente en ese momento (actualmente 5,99 €), a menos que la canceles antes.',
		},
	],
	GB: [
		{
			ribbon: 'OFFER',
			offerText: 'OFFER',
			longText: '£1.99/mo for 4 months*. T&Cs apply',
			startDate: '2025-06-11',
			endDate: '2025-06-30',
			provider: 337,
			disclaimerText:
				'Offer applies to Standard with Ads. Offer ends 30/06/2025. New and eligible returning subscribers only. Must be 18+. After 4 months auto renews at the then-current Standard with Ads price of £4.99 a month unless you cancel before then.',
		},
	],
	AU: [
		{
			ribbon: 'Offer',
			offerText: 'Limited Offer',
			longText: '50% off BINGE for 5 months!',
			startDate: '2025-06-13',
			endDate: '2025-07-15',
			provider: 385,
			disclaimerText: 'New and Returning Customers Only. Offer Ends July 15th.',
			offerDisclaimerText: 'New and Returning Customers Only. Offer Ends July 15th. BINGE Basic only.',
		},
	],
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: ComputedRef<PromoTextWithIcon>;
} {
	const { setProviderPromotion, providerPromotion } = useTitleDetailsStore();

	onServerPrefetch(async () => await setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(async () => await setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFree.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.packageId
		);
	}

	return {
		/** Is a provider promotion applicable to the current title. */
		titleHasProviderPromotion,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const texts = promotions[country as PromotionCountries];

	if (!texts || texts.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = texts.find(promo =>
		title.offers.some(
			offer => flatrateOrFree.includes(offer.monetizationType) && offer.package.packageId === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}

// Disney External Pixel Tracking
const oceaniaLocales = new Set([WebLocale.au, WebLocale.nz]);
const noramLocales = new Set([WebLocale.us, WebLocale.ca]);
const latamLocales = new Set([
	WebLocale.br,
	WebLocale.mx,
	WebLocale.ar,
	WebLocale.cl,
	WebLocale.co,
	WebLocale.pe,
	WebLocale.uy,
	WebLocale.ec,
	WebLocale.pa,
	WebLocale.cr,
	WebLocale.py,
	WebLocale.bo,
	WebLocale.gt,
	WebLocale.ni,
	WebLocale.do,
	WebLocale.sv,
	WebLocale.hn,
	WebLocale.ve,
]);

export function getDisneyImpressionUrl(packageId: number | null | undefined, webLocale: WebLocale) {
	if (packageId === 337 /* Disney+ */) {
		if (oceaniaLocales.has(webLocale) || noramLocales.has(webLocale))
			return 'https://imp.pxf.io/i/1206980/1833790/9358';

		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1848147/9358';
	}

	if (packageId === 619 /* Star+ */) {
		if (latamLocales.has(webLocale)) return 'https://imp.pxf.io/i/1206980/1584316/13591';
	}

	return null;
}

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;
